import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { APIBASE } from '../../constants/api.constants'
import { APIROUTES } from '../../constants/routes.constants'

/**
 * useTelemetryTracker — fires /telemetry/pageview on every route change
 * and /telemetry/exit (via sendBeacon) on tab close / navigation away.
 * Mounted once near the top of the routing tree. Kiran 2026-06-30.
 *
 * Privacy:
 *   - DNT=1 visitors short-circuit BEFORE the first fetch — no network
 *     trace of their visit.
 *   - The handler honours DNT too as a defence-in-depth; if a future
 *     consumer pipeline ever bypasses this hook, the server still
 *     drops the event.
 *
 * Behaviour:
 *   - First mount: send pageview with no previous dwell.
 *   - Subsequent route changes: send pageview with the elapsed ms
 *     between the previous entered_at and now.
 *   - beforeunload: send exit beacon with the in-flight page's dwell.
 *
 * Uses navigator.sendBeacon for the exit event because regular fetch
 * gets cancelled during page unload on most browsers.
 */
export function useTelemetryTracker(): null {
  const location = useLocation()
  // Wall-clock when the current page was entered — used to compute
  // dwell on every navigation (next pageview) AND on tab close.
  const enteredAtRef = useRef<number>(typeof performance !== 'undefined' ? performance.now() : Date.now())
  // The path the SPA is currently rendering — kept in a ref so the
  // exit beacon listener (which sees a stale closure) can read the
  // latest path at the moment of unload.
  const currentPathRef = useRef<string>(location.pathname + location.search)
  // Honour the Do-Not-Track flag baked into the browser.
  const dntRef = useRef<boolean>(typeof navigator !== 'undefined' && (navigator as { doNotTrack?: string }).doNotTrack === '1')

  // Fire a pageview on every route change.
  useEffect(() => {
    if (dntRef.current) return
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now()
    const previousDwell = currentPathRef.current === '' ? null : Math.round(now - enteredAtRef.current)
    const path = location.pathname + location.search
    const params = new URLSearchParams(location.search)
    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : null
    const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : null
    const connectionType =
      typeof navigator !== 'undefined'
        ? (navigator as { connection?: { effectiveType?: string } }).connection?.effectiveType ?? null
        : null
    const body = {
      path,
      previous_path_dwell_ms: previousDwell !== null && currentPathRef.current === path ? null : previousDwell,
      viewport_width: viewportWidth,
      viewport_height: viewportHeight,
      connection_type: connectionType,
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content')
    }
    // Fire-and-forget. We don't await; pageview failure mustn't block
    // navigation. Credentials so the session cookie tags the event.
    fetch(`${APIBASE}${APIROUTES.TELEMETRYPAGEVIEW}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      credentials: 'include',
      keepalive: true
    }).catch(() => undefined)
    enteredAtRef.current = now
    currentPathRef.current = path
  }, [location.pathname, location.search])

  // Bind the exit beacon once — it reads the ref values at fire time
  // so it stays accurate as the visitor navigates around.
  useEffect(() => {
    if (dntRef.current) return
    const fireExit = (): void => {
      const now = typeof performance !== 'undefined' ? performance.now() : Date.now()
      const dwellMs = Math.round(now - enteredAtRef.current)
      const body = JSON.stringify({ dwell_ms: dwellMs })
      const url = `${APIBASE}${APIROUTES.TELEMETRYEXIT}`
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        try {
          navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }))
          return
        } catch {
          // Fall through to keepalive fetch.
        }
      }
      void fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        credentials: 'include',
        keepalive: true
      }).catch(() => undefined)
    }
    // pagehide fires more reliably than beforeunload on mobile + when
    // tabs are bfcached. visibilitychange→hidden covers tab-switch on
    // mobile Safari where pagehide is suppressed for bfcache.
    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') fireExit()
    }
    window.addEventListener('pagehide', fireExit)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.removeEventListener('pagehide', fireExit)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [])

  return null
}
